import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Sidebar} from "primereact/sidebar";
import Icon from "../../../system/components/icon";
import {Divider} from "primereact/divider";
import {Button} from "primereact/button";
import {useTranslate} from "../../../system/useTranslate";
import {Messages} from "primereact/messages";
import Span from "../../../system/components/span";
import DataFieldItem from "../../../system/components/data-field-item";
import Avatar from "../../../system/components/avatar";
import {useDispatch, useSelector} from "react-redux";
import {ListBox} from "primereact/listbox";
import {fileThumbnail, isPast, timeSince} from "../../../system/utils";
import UploadDialog from "../../components/upload-dialog";
import SelectMember from "../../tasks/components/select-member";
import MultiSelectProjects from "../../tasks/components/multi-select-projects";
import SelectZofinoDateTime from "../../tasks/components/select-zofino-datetime";
import TaskDelayModal from "../../tasks/components/task-delay-modal";
import SelectTaskType from "../../tasks/components/select-task-type";
import {isEmptyArray} from "formik";
import BoxHeader from "../../components/box-header";
import {DataView} from "primereact/dataview";
import Attachments from "../../tasks/components/attachments";
import SelectType from "../../tasks/components/select-type";
import {Menu} from "primereact/menu";
import {useGoalComment} from "../../../models/goal-comment/use-goal-comment";
import {useTaskComment} from "../../../models/task-comment/use-task-comment";
import AddSubTask from "../../tasks/components/add-subtask";
import SelectMembers from "../../tasks/components/select-members";
import {BreadCrumb} from "primereact/breadcrumb";
import {usePermission} from "../../../system/usePermission";
import MultiSelectProjectsDeliverables from "../../tasks/components/multi-select-projects-deliverables";
import SelectCustomer from "../../tasks/components/select-customer";
import {CustomerApi} from "../../../models/crm/crm-api";
import TaskFocusControls from "../../../system/focus/task-focus-controls";
import {useFocusSession} from "../../../system/focus/focus-session-context";
import ChatComposer, {mentionToken} from "../../inbox/chat-composer";
import {MessageContent} from "../../inbox/chat-message";
import ChatMessageAttachment from "../../inbox/chat-message-attachment";
import {chatMessageAttachmentIdentifiers} from "../../inbox/chat-message-attachment-payload";
import {useTask} from "../../../models/task/use-task";
import {upsertWorkspaceTask} from "../../../system/workspace-graph-slice";
import {useRecurrenceScope} from "../../tasks/components/recurrence-scope-dialog";
import {createDefaultRecurrence, isRecurring, recurrenceErrors, recurrenceFromSeries, recurrenceSummary, sameRecurrence} from "../../tasks/components/task-recurrence-model";
import TaskSeries from "../../../models/task-series/task-series";
import Task from "../../../models/task/task";
import {setMessage} from "../../../system/app-slice";
import EditableComment from "../../components/editable-comment";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import {Chips} from "primereact/chips";
import {Dropdown} from "primereact/dropdown";
import {PRIORITY_OPTIONS} from "../../tasks/components/task-filter-model";
import {normalizeFreeBoardColumns} from "../tabs/free-board-model";
import {RichTextField} from "../../../system/components/rich-text";
import TaskDecisionTag from "../meetings/task-decision-tag";
import TaskFormTag from "../../../modules/forms/task-form-tag";

const customerApi = new CustomerApi();

const TEMPLATE_FIELDS = ["title", "description", "assignee", "members", "projects", "deliverables", "task_type", "startAt", "dueDate", "duration", "scheduleType"];

function TaskSidebar({selectedTask, setSelectedTask, updateTask: updateTaskProp, onOtherTaskClicked, disabled, boardColumns = null}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const messages = useRef();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const {user} = useSelector(state => state.auth);
    const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
    const [delayModalVisible, setDelayModalVisible] = useState(false);
    const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
    const deletingAttachmentRef = useRef(false);
    const [viewPopupVisible, setViewPopupVisible] = useState(false);
    const [seenBy, setSeenBy] = useState([]);
    const visitRegistrationRef = useRef(false);
    const viewPopupRef = useRef();
    const [subTasks, setSubTasks] = useState([]);
    const [checklist, setChecklist] = useState([]);
    const moreMenu = useRef();
    const permission = usePermission();
    const canAccessCrm = permission.can("customers.read");
    const [customers, setCustomers] = useState([]);
    const focus = useFocusSession();
    const taskModel = useTask();
    const taskModelRef = useRef(taskModel);
    taskModelRef.current = taskModel;

    const workspaceId = workspaceMembershipData?.workspace?.id;
    const selectedTaskKey = selectedTask?.documentId ?? selectedTask?.id;
    const hasTaskDetails = Boolean(
        selectedTask?.__detailsLoaded,
    );
    const focusOwned = focus.isTaskActive(selectedTask);
    const focusPresence = focusOwned ? focus.session : focus.getTaskPresence(selectedTask);
    const focusLocked = Boolean(focusPresence);
    const editingDisabled = disabled || focusLocked;
    const canUpdateTaskStatus = Boolean(
        selectedTask?.assignee && workspaceMembershipData &&
        String(selectedTask.assignee.documentId || selectedTask.assignee.id)
        === String(workspaceMembershipData.documentId || workspaceMembershipData.id),
    );
    const membershipIdentifier = String(workspaceMembershipData?.documentId || workspaceMembershipData?.id || "");
    const privacyMemberIdentifiers = [
        selectedTask?.creatorUser,
        selectedTask?.assignee,
        ...(selectedTask?.members || []),
    ].filter(Boolean).map((membership) => String(membership?.documentId || membership?.id || ""));
    const canChangePrivacy = Boolean(membershipIdentifier && privacyMemberIdentifiers.includes(membershipIdentifier));
    const selectedProjectReference = selectedTask?.projects?.[0];
    const selectedProjectKey = String(selectedProjectReference?.documentId || selectedProjectReference?.id || "");
    const selectedProject = (workspaceMembershipData?.workspace?.projects || []).find((project) => (
        String(project?.documentId || project?.id || "") === selectedProjectKey
    )) || selectedProjectReference;
    const projectBoardColumns = useMemo(() => (
        Array.isArray(boardColumns) && boardColumns.length
            ? boardColumns
            : (selectedProjectKey ? normalizeFreeBoardColumns(selectedProject?.freeBoardColumns, translate) : [])
    ), [boardColumns, selectedProject?.freeBoardColumns, selectedProjectKey, translate]);
    const boardColumnOptions = useMemo(() => projectBoardColumns.map((column) => ({
        value: String(column.id),
        label: column.title || column.label,
        taskStatus: column.taskStatus || "todo",
    })), [projectBoardColumns]);
    const selectedBoardColumn = boardColumnOptions.find((column) => column.value === String(selectedTask?.freeBoardColumnId || ""));

    useEffect(() => {
        if (!selectedTaskKey || hasTaskDetails) return undefined;
        let active = true;
        taskModelRef.current.getTask(selectedTaskKey).then((fullTask) => {
            if (active && fullTask) dispatch(upsertWorkspaceTask(fullTask));
        }).catch(() => {});
        return () => { active = false; };
    }, [dispatch, hasTaskDetails, selectedTaskKey]);

    useEffect(() => {
        if (selectedTaskKey && canAccessCrm && workspaceId) {
            customerApi.list({page: 1, pageSize: 100})
                .then((response) => setCustomers(response?.data || []))
                .catch(() => setCustomers([]));
        }
    }, [canAccessCrm, selectedTaskKey, workspaceId]);

    useEffect(() => {
        setViewPopupVisible(false);
        setSeenBy(Array.isArray(selectedTask?.seenBy) ? selectedTask.seenBy : []);
        visitRegistrationRef.current = false;
    }, [selectedTask]);

    useEffect(() => {
        if (!viewPopupVisible) return undefined;

        const closeOnOutsideClick = (event) => {
            if (!viewPopupRef.current?.contains(event.target)) setViewPopupVisible(false);
        };

        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, [viewPopupVisible]);

    useEffect(() => {
        if (!selectedTask) return undefined;
        const closeOrKeepForTask = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest(".p-sidebar")) return;
            if (target.closest("[data-task-sidebar-trigger='true']")) return;
            if (target.closest("[data-zofino-floating-layer]")) return;
            if (target.closest(".p-dialog, .p-overlaypanel, .p-dropdown-panel, .p-multiselect-panel, .p-datepicker, .p-menu, .p-tooltip")) return;
            setSelectedTask(undefined);
        };
        document.addEventListener("mousedown", closeOrKeepForTask);
        return () => document.removeEventListener("mousedown", closeOrKeepForTask);
    }, [selectedTask, setSelectedTask]);

    const toggleViewPopup = () => {
        if (!viewPopupVisible && !visitRegistrationRef.current && user?.id) {
            visitRegistrationRef.current = true;
            setSeenBy((current) => {
                const alreadySeen = current.some((view) => (view?.user?.id ?? view?.id) === user.id);
                if (alreadySeen) return current;

                return [...current, {user, seenAt: new Date().toISOString(), localOnly: true}];
            });
            // TODO: Persist this unique visit through the confirmed task seenBy backend contract when available.
        }

        setViewPopupVisible((current) => !current);
    };

    const {askScope, scopeDialog} = useRecurrenceScope();
    const isRecurringTask = Boolean(selectedTask?.series);
    const storedRecurrence = useMemo(
        () => (selectedTask?.series ? recurrenceFromSeries(selectedTask.series) : createDefaultRecurrence()),
        [selectedTask?.series],
    );
    const [recurrenceDraft, setRecurrenceDraft] = useState(null);
    useEffect(() => { setRecurrenceDraft(null); }, [selectedTask?.documentId]);

    const notify = (severity, content) => dispatch(setMessage({severity, content}));
    const recurrenceError = (error) => {
        const code = error?.details?.code || error?.code || error?.message;
        return typeof code === "string" && code.startsWith("recurrence.") ? translate(code) : translate("task-delay.submit-error");
    };

    // Editing a recurring occurrence asks which occurrences the change applies to (this / following / all).
    const updateTask = async (task, data, ...rest) => {
        if (task?.series && data && !data.recurrenceScope && TEMPLATE_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(data, field))) {
            const scope = await askScope("edit");
            if (!scope) return;
            return updateTaskProp(task, {...data, recurrenceScope: scope}, ...rest);
        }
        return updateTaskProp(task, data, ...rest);
    };

    // Rule changes made in the date picker are applied when the picker closes.
    const applyRecurrenceDraft = async () => {
        const draft = recurrenceDraft;
        setRecurrenceDraft(null);
        if (!draft || sameRecurrence(draft, storedRecurrence)) return;
        if (isRecurring(draft) && recurrenceErrors(draft).length) {
            notify("warn", translate(recurrenceErrors(draft)[0]));
            return;
        }
        if (!isRecurring(draft) && !isRecurringTask) return;
        if (isRecurring(draft) && !selectedTask?.dueDate) {
            notify("warn", translate("recurrence.error.due-date-required"));
            return;
        }
        let scope = null;
        if (isRecurringTask) {
            scope = await askScope("rule", isRecurring(draft) ? ["following", "all"] : ["all"]);
            if (!scope) return;
        }
        try {
            const updated = await taskModelRef.current.updateTask(selectedTask.documentId, {
                recurrence: draft,
                ...(scope ? {recurrenceScope: scope} : {}),
            }, {throwOnError: true});
            if (updated) {
                dispatch(upsertWorkspaceTask(updated));
            }
        } catch (error) {
            notify("error", recurrenceError(error));
        }
    };

    const removeLocally = (task) => {
        dispatch(upsertWorkspaceTask({...task, archivedAt: new Date().toISOString()}));
        setSelectedTask?.(undefined);
    };

    // a deleted task leaves every list at once and its sidebar closes (it stayed until a reload — boss's bug report
    // 2026-10-28); nothing changes when the server refused the delete
    const archiveTask = async () => {
        const task = selectedTask;
        const result = await updateTaskProp(task, {archivedAt: new Date(), archivedBy: workspaceMembershipData});
        if (result) removeLocally(task);
    };

    const deleteTaskCommand = async () => {
        if (!selectedTask?.series) {
            await archiveTask();
            return;
        }
        const scope = await askScope("delete");
        if (!scope) return;
        if (scope === "this") {
            // "Only this occurrence" keeps the existing archive behaviour (the slot is not recreated).
            await archiveTask();
            return;
        }
        try {
            await new Task().deleteWithScope(selectedTask.documentId, scope);
            removeLocally(selectedTask);
        } catch (error) {
            notify("error", recurrenceError(error));
        }
    };

    const skipCommand = async () => {
        try {
            const model = new Task();
            const skipped = selectedTask?.occurrenceStatus === "skipped";
            const res = skipped ? await model.unskip(selectedTask.documentId) : await model.skip(selectedTask.documentId);
            if (res?.data) {
                dispatch(upsertWorkspaceTask({...selectedTask, ...res.data}));
            }
        } catch (error) {
            notify("error", recurrenceError(error));
        }
    };

    const seriesCommand = async (action) => {
        try {
            const model = new TaskSeries();
            const seriesId = selectedTask.series.documentId;
            if (action === "pause") await model.pause(seriesId);
            if (action === "resume") await model.resume(seriesId, "schedule");
            const refreshed = await taskModelRef.current.getTask(selectedTask.documentId);
            if (refreshed) {
                dispatch(upsertWorkspaceTask(refreshed));
            }
        } catch (error) {
            notify("error", recurrenceError(error));
        }
    };

    const Header = ({disabled})=>{

        const seriesStatus = selectedTask?.series?.seriesStatus;
        const moreMenuItems = [
            ...(isRecurringTask ? [
                {label: translate(selectedTask?.occurrenceStatus === "skipped" ? "recurrence.menu.unskip" : "recurrence.menu.skip"), command: skipCommand},
                ...(seriesStatus === "paused"
                    ? [{label: translate("recurrence.menu.resume"), command: () => seriesCommand("resume")}]
                    : seriesStatus === "active" ? [{label: translate("recurrence.menu.pause"), command: () => seriesCommand("pause")}] : []),
            ] : []),
            {label:translate("delete"), command: deleteTaskCommand}
        ]

        return(
            <div className={`p-3 flex align-items-center justify-content-between border-bottom-1 border-300 w-full h-4rem rtl`}>

                <div className="flex align-items-center  ">
                    <Button disabled={disabled || (focusLocked && !focusOwned) || !canUpdateTaskStatus} onClick={()=>{
                        if (!canUpdateTaskStatus) {
                            messages?.current?.replace({
                                severity:'warn',
                                summary: 'فقط مسئول این کار اجازه تغییر وضعیت را دارد.',
                                sticky:false,
                                life:3000
                            });
                            return;
                        }

                        updateTask(selectedTask, {doneAt:selectedTask?.taskStatus==="done"?null:Date.now(), taskStatus:selectedTask?.taskStatus==="done"?"todo":"done"})}} className="flex-row-reverse p-button-secondary p-button-outlined p-2 pr-2 pl-2 text-xs border-round-xl flex align-items-center gap-2" label={translate(selectedTask?.taskStatus==="done"?"task-done":"set-task-done")}>
                        <Icon icon={`${selectedTask?.taskStatus==="done"?"mi mi:check_circle":"mi mi:radio_button_unchecked"}`}/>
                    </Button>
                </div>
                <div className={`flex align-items-center gap-4 `}>
                    <div className={`flex align-items-center gap-4 ${editingDisabled && " p-disabled"}  ${selectedTask?.taskStatus==="done"&&"p-disabled"}`}>
                        <div className="relative flex align-items-center justify-content-center line-height-1" ref={viewPopupRef}>
                            <Icon icon="mi mi:visibility" onClick={toggleViewPopup}/>
                            {viewPopupVisible && (
                                <TaskViewPopup task={selectedTask} seenBy={seenBy}/>
                            )}
                        </div>
                        <Icon icon="mi mi:thumb_up" onClick={()=>{setSelectedTask(undefined)}}/>
                        <Icon icon="mi mi:link" onClick={()=>{setSelectedTask(undefined)}}/>
                        <Icon icon="mi mi:attach_file"  onClick={()=>{setUploadDialogVisible(true)}}/>
                        <Icon icon="mi mi:more_horiz"  onClick={(e)=>{moreMenu.current.toggle(e)}}/>
                        <Menu pt={{root:()=>({className:"w-fit p-1"}), menuitem:()=>({className:"text-sm rtl"})}} model={moreMenuItems} popup ref={moreMenu} />
                        <span className="h-1rem border-right-2 border-300"/>
                    </div>
                    <Icon icon="mi mi:close_small" className="text-3xl" onClick={()=>{setSelectedTask(undefined)}}/>
                </div>

                <UploadDialog entityType="task" entityDocumentId={selectedTask?.documentId} visible={uploadDialogVisible} setVisible={setUploadDialogVisible} updateContentType={(e)=>{updateTask(selectedTask, {attachments: {connect:e.map(e=>({id:e.id}))}})}}/>
            </div>
        )
    }


    useEffect(()=>{
        setSubTasks(selectedTask?.childrenTasks)
        setChecklist(Array.isArray(selectedTask?.checklist) ? selectedTask.checklist : []);
        // console.log(workspaceMembershipData)
        // messages?.current?.replace({severity:'info', content: <div>test</div>, sticky:true, closable:true, multiple:false});
    },[selectedTask])

    const getParentTasks = (task) => {
        const parents = [];

        let current = task?.parentTask;

        while (current) {
            parents.push({
                id: current.id,
                label: current.title,
                command:(data)=>{
                    onOtherTaskClicked?.(data.item)
                }
            });

            current = current.parentTask;
        }
        return parents.sort((a,b)=>(a.id-b.id));
    };

    const modifyProjects = (nextProjects = []) => {
        const prevIds = (selectedTask?.projects ?? []).map(p => p.id);
        const nextIds = nextProjects.map(p => p.id);

        const connect = nextIds.filter(id => !prevIds.includes(id));
        const disconnect = prevIds.filter(id => !nextIds.includes(id));

        if (!connect.length && !disconnect.length) return;

        updateTask(selectedTask, {
            projects: {
                ...(connect.length ? {connect} : {}),
                ...(disconnect.length ? {disconnect} : {})
            }
        });
    };


    const modifyDeliverables = (nextDeliverables = []) => {
        const prevIds = (selectedTask?.deliverables ?? []).map(d => d.id);
        const nextIds = nextDeliverables.map(d => d.id);

        const connect = nextIds.filter(id => !prevIds.includes(id));
        const disconnect = prevIds.filter(id => !nextIds.includes(id));

        updateTask(selectedTask, {
            deliverables: {
                ...(connect.length ? {connect} : {}),
                ...(disconnect.length ? {disconnect} : {})
            }
        });
    };

    const deleteAttachment = async (attachment) => {
        if (disabled || deletingAttachmentRef.current || !attachment?.id) return;

        deletingAttachmentRef.current = true;
        setDeletingAttachmentId(attachment.id);

        try {
            await updateTask(selectedTask, {
                attachments: {disconnect: [{id: attachment.id}]}
            });
        } finally {
            deletingAttachmentRef.current = false;
            setDeletingAttachmentId(null);
        }
    };




    // const modifyDeliverables = (data)=>{
    //
    //     if (data?.status==="added"){
    //         //Each selectedTask has multiple deliverables, but the deliverables might not has same projectIds
    //         updateTask(selectedTask, {deliverables:{connect:[data.deliverableData.id]}})
    //     }
    //     if (data?.status==="removed"){
    //         updateTask(selectedTask, {deliverables:{disconnect:[data.deliverableData.id]}})
    //     }
    // }


    return (
        <Sidebar  dir="rtl " header={<Header  disabled={disabled}/>} showCloseIcon={false} dismissable={false} modal={false} visible={selectedTask!==undefined} className={` w-5 rtl`} onHide={()=>{setSelectedTask(undefined)}} pt={{header: () => ({style: {padding: 0, margin: 0}}), content: () => ({style: {padding: 0}})}}>
            <div className="flex flex-column align-items-center h-full min-h-0 overflow-hidden">
                <div className="w-full px-3 pt-3 flex-shrink-0">
                    <TaskFocusControls task={selectedTask} showHistory={false}/>
                </div>
                <div
                    className="flex-1 min-h-0 flex flex-column align-items-center w-full overflow-auto scrollbar-hide p-3">
                    <div className={`w-full flex flex-column align-items-center ${editingDisabled || selectedTask?.taskStatus === "done" ? "p-disabled" : ""}`}>
                    <Messages className="w-full" ref={messages}/>
                    <Message
                        title={translate(selectedTask?.isPrivate ? "private-task" : "public-task")}
                        icon={<Icon icon={selectedTask?.isPrivate ? "mi mi:lock" : "mi mi:lock_open"}/>}
                        left={<Button
                        type="button"
                        disabled={editingDisabled || !canChangePrivacy}
                        onClick={() => updateTask(selectedTask, {isPrivate: !selectedTask?.isPrivate})}
                        className="text-xs p-button-danger p-button-text p-1 pl-3 pr-3 border-none shadow-none "
                        label={translate(selectedTask?.isPrivate ? "set-public" : "set-private")}/>}
                    />
                    <div className="w-full rtl pt-3">
                        {/*<Span spanClassName="text-2xl font-bold">{}</Span>*/}
                        <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left" className="text-sm"/>}
                                    className="bg-transparent border-none text-2xs font-bold p-2"
                                    pt={{label: () => ({className: "text-400"})}} model={getParentTasks(selectedTask)}/>

                        <DataFieldItem className="text-2xl font-bold w-full pt-2 pb-2" value={selectedTask?.title}
                                       onKeyDownEnter={(e) => {
                                           updateTask(selectedTask, {title: e.value})
                                       }}/>
                        {/* the meeting decision this task carries out (docs/tasks/27-meetings-feedback.md) */}
                        <TaskDecisionTag task={selectedTask} className="mx-2"/>
                    </div>
                    <div className="flex flex-column w-full mt-4 ">
                        <LabelValue label={translate("created-by")} alignIcon>
                            <div className="flex flex-column align-items-start gap-1 min-w-0 w-full">
                                <div className="flex align-items-center gap-2">
                                    <Avatar
                                        memberData={selectedTask?.creatorUser?.member}
                                        className="text-3xs"
                                        style={{width: "1.48rem", height: "1.48rem"}}
                                    />
                                    <span className="text-xs">{selectedTask?.creatorUser?.member?.fullname ?? "---"}</span>
                                </div>
                                <TaskFormTag task={selectedTask} detailed/>
                            </div>
                        </LabelValue>
                        <LabelValue label={translate("assignee")} alignIcon>
                            <SelectMember selectedMember={selectedTask?.assignee}
                                          members={workspaceMembershipData?.workspace.workspace_memberships || []}
                                          onChange={(membership) => {
                                              updateTask(selectedTask, {assignee: membership})
                                          }}/>
                        </LabelValue>
                        <LabelValue label={translate("dueDate")} alignIcon>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <SelectZofinoDateTime enableRecurrence range assistant={{assignee: selectedTask?.assignee, excludeTaskKey: selectedTask?.documentId}} selectedDateTime={{
                                    start: selectedTask?.startAt ? new Date(selectedTask?.startAt).getTime() / 1000 : null,
                                    end: selectedTask?.dueDate ? new Date(selectedTask?.dueDate).getTime() / 1000 : null
                                }} recurrence={recurrenceDraft || storedRecurrence}
                                   onRecurrenceChange={setRecurrenceDraft}
                                   onPanelHide={applyRecurrenceDraft}
                                   onChange={(e) => {
                                    updateTask(selectedTask, {
                                        startAt: e.start ? new Date(e.start?.toUnix() * 1000) : null,
                                        dueDate: e.end ? new Date(e.end?.toUnix() * 1000) : null
                                    })
                                }}/>
                                {isRecurringTask && (
                                    <span className="flex align-items-center gap-1 text-2xs text-600">
                                        <Icon icon="mi mi:repeat" className="text-sm text-primary"/>
                                        <span>{recurrenceSummary(selectedTask.series, translate, selectedTask.scheduledDueAt)}</span>
                                        {selectedTask?.recurrence?.isDeferred && <span className="text-orange-600">· {translate("date-picker.recurrence.deferred")}</span>}
                                        {selectedTask?.occurrenceStatus === "skipped" && <span className="text-500">· {translate("recurrence.skipped")}</span>}
                                        {selectedTask?.series?.seriesStatus === "paused" && <span className="text-500">· {translate("recurrence.paused")}</span>}
                                    </span>
                                )}
                                {selectedTask?.dueDate && selectedTask?.taskStatus !== "done" && !selectedTask?.archivedAt && (
                                    <Button
                                        type="button"
                                        disabled={editingDisabled}
                                        onClick={() => setDelayModalVisible(true)}
                                        className="p-button-text p-button-secondary p-1 w-2rem h-2rem"
                                        icon={<Icon icon="mi mi:history" className="text-lg"/>}
                                        aria-label={translate("task-delay.title")}
                                        tooltip={translate("task-delay.title")}
                                        tooltipOptions={{position: "top"}}
                                        style={{
                                            border: 'none',
                                            boxShadow: 'none',
                                            outline: 'none',
                                        }}
                                    />
                                )}
                            </div>
                        </LabelValue>
                        <LabelValue label={translate("projects")} alignIcon>
                            <MultiSelectProjects
                                value={selectedTask?.projects ?? []}
                                options={workspaceMembershipData?.workspace?.projects ?? []}
                                onChange={modifyProjects}
                            />

                            {/*<MultiSelectProjects chips={true} selectedProjects={isEmptyArray(selectedTask?.projects)?null:selectedTask?.projects} projects={workspaceMembershipData?.workspace.projects} onChange={(e)=>{updateTask(selectedTask, {projects: e??null})}}/>*/}
                        </LabelValue>
                        {boardColumnOptions.length > 0 && (
                            <LabelValue label={translate("free-board.task-column")} alignIcon>
                                <Dropdown
                                    dir="rtl"
                                    disabled={editingDisabled}
                                    className="w-auto max-w-full text-xs border-none shadow-none rtl"
                                    panelClassName="rtl text-xs"
                                    value={selectedBoardColumn?.value ?? null}
                                    options={boardColumnOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    placeholder={translate("free-board.select-column")}
                                    valueTemplate={(option, props) => (
                                        <span className="text-xs text-right white-space-nowrap">
                                            {option?.label || props.placeholder}
                                        </span>
                                    )}
                                    pt={{input: {className: "text-xs text-right py-2 px-0"}, trigger: {className: "hidden"}, item: {className: "text-xs text-right"}}}
                                    onChange={(event) => {
                                        const column = boardColumnOptions.find((item) => item.value === event.value);
                                        if (!column) return;
                                        updateTask(selectedTask, {
                                            freeBoardColumnId: column.value,
                                            taskStatus: column.taskStatus,
                                            doneAt: column.taskStatus === "done" ? (selectedTask?.doneAt || Date.now()) : null,
                                        });
                                    }}
                                />
                            </LabelValue>
                        )}
                        <LabelValue label={translate("deliverable")} alignIcon>
                            <SelectDeliverables
                                projects={selectedTask?.projects ?? []}
                                deliverables={selectedTask?.deliverables ?? []}
                                onChange={modifyDeliverables}
                            />
                            {/*<SelectDeliverables projects={selectedTask?.projects} deliverables={selectedTask?.deliverables} onChange={(e)=>{modifyDeliverables(e)}} />*/}
                        </LabelValue>
                        <LabelValue label={translate("task-type")} alignIcon>
                            <SelectTaskType selectedTaskType={selectedTask?.task_type}
                                            taskTypes={workspaceMembershipData?.workspace.task_types} onChange={(e) => {
                                updateTask(selectedTask, {task_type: e?.id ?? null})
                            }}/>
                        </LabelValue>
                        <LabelValue label={translate("task.priority")} alignIcon>
                            <Dropdown
                                value={selectedTask?.priority ?? null}
                                options={PRIORITY_OPTIONS}
                                optionValue="id"
                                optionLabel="label"
                                showClear
                                disabled={editingDisabled}
                                placeholder={translate("task.priority.placeholder")}
                                className="w-full text-xs task-priority-dropdown"
                                itemTemplate={(option) => <span className="flex align-items-center gap-2 text-xs"><span className="border-circle" style={{width: ".55rem", height: ".55rem", background: option.color}}/>{option.label}</span>}
                                valueTemplate={(option, props) => option ? <span className="flex align-items-center gap-2 text-xs"><span className="border-circle" style={{width: ".55rem", height: ".55rem", background: option.color}}/>{option.label}</span> : <span className="text-xs text-500">{props.placeholder}</span>}
                                onChange={(event) => updateTask(selectedTask, {priority: event.value ?? null})}
                            />
                        </LabelValue>
                        <LabelValue label={translate("task.tags")} alignIcon>
                            <Chips
                                value={Array.isArray(selectedTask?.tags) ? selectedTask.tags : []}
                                onChange={(e) => {
                                    const tags = Array.from(new Set((e.value || []).map((tag) => String(tag).trim().replace(/^#/, "")).filter(Boolean)));
                                    updateTask(selectedTask, {tags});
                                }}
                                separator=","
                                placeholder={translate("task.tags.placeholder")}
                                className="w-full text-xs task-tags-chips"
                                allowDuplicate={false}
                            />
                        </LabelValue>
                        <LabelValue label={translate("involved-members")} alignIcon>
                            <SelectMembers allowEmpty={true} className="p-1"
                                           selectedMembers={selectedTask?.members || []}
                                           members={workspaceMembershipData?.workspace.workspace_memberships || []}
                                           onChange={(e) => {
                                               updateTask(selectedTask, {members: e.members})
                                           }}/>
                        </LabelValue>
                        {canAccessCrm && (
                            <LabelValue label={translate("customer")} alignIcon>
                                <SelectCustomer allowEmpty={true} className="p-1"
                                                selectedCustomer={selectedTask?.customer} customers={customers}
                                                onChange={(selectedCustomer) => {
                                                    updateTask(selectedTask, {customer: selectedCustomer?.id ?? null})
                                                }}/>
                            </LabelValue>
                        )}
                        <LabelValue label={translate("permit")}>
                            <div className="w-full">
                                <DataFieldItem
                                    longText
                                    disabled={editingDisabled}
                                    placeholder={translate("task.assignee-authority.placeholder")}
                                    className="text-xs w-full py-2"
                                    value={selectedTask?.assigneeAuthority ?? ""}
                                    onKeyDownEnter={(event) => {
                                        const value = String(event.value ?? "").trim();
                                        if (value !== (selectedTask?.assigneeAuthority ?? "")) {
                                            updateTask(selectedTask, {assigneeAuthority: value});
                                        }
                                    }}
                                />
                            </div>
                        </LabelValue>
                        <LabelValue label={translate("result")}>
                            <div className="w-full">
                                <DataFieldItem
                                    longText
                                    disabled={editingDisabled}
                                    placeholder={translate("task.expected-result.placeholder")}
                                    className="text-xs w-full py-2"
                                    value={selectedTask?.expectedResult ?? ""}
                                    onKeyDownEnter={(event) => {
                                        const value = String(event.value ?? "").trim();
                                        if (value !== (selectedTask?.expectedResult ?? "")) {
                                            updateTask(selectedTask, {expectedResult: value});
                                        }
                                    }}
                                />
                            </div>
                        </LabelValue>
                        <LabelValue label={translate("task-checklist")}>
                            <TaskChecklist
                                items={checklist}
                                disabled={editingDisabled}
                                onChange={(items) => {
                                    setChecklist(items);
                                    updateTask(selectedTask, {checklist: items});
                                }}
                            />
                        </LabelValue>
                        <Attachments
                            attachments={selectedTask?.attachments ?? []}
                            onAddClicked={() => {
                                setUploadDialogVisible(true)
                            }}
                            onDeleteClicked={deleteAttachment}
                            deletingAttachmentId={deletingAttachmentId}
                            disabled={disabled}
                        />
                        <LabelValue label={translate("task-description")}>
                            <div className="w-full text-xs pt-2 pb-2">
                                <RichTextField value={selectedTask?.description} disabled={disabled}
                                               placeholder={translate("task-description-placeholder")}
                                               onSave={(html) => updateTask(selectedTask, {description: html})}/>
                            </div>
                        </LabelValue>
                        <LabelValue label={translate("subtasks")}>
                            <div className="overflow-auto max-h-12rem w-full">
                                {subTasks?.length > 0 ? (
                                    <div className="flex align-items-end p-2 gap-2">
                                        <div className="flex flex-column w-full">
                                            {subTasks.map((sb, index) => (<AddSubTask onClick={(e) => {
                                                onOtherTaskClicked?.(e)
                                            }} onSave={(e) => {
                                                updateTask(selectedTask, null, [{...e, assignee: e.assignee}])
                                            }} key={sb.id} payload={{subTaskData: sb}} onChange={(e) => {
                                                setSubTasks(prv => {
                                                    let _prv = [...prv];
                                                    _prv[index] = {...e, assignee: e.assignee}
                                                    return _prv;
                                                })
                                            }} onDelete={() => {
                                                setSubTasks(prv => {
                                                    let _prv = [...prv];
                                                    _prv = _prv.filter((_, i) => i !== index)
                                                    return _prv;
                                                })
                                            }}/>))}
                                            <Icon onClick={() => {
                                                setSubTasks([...subTasks, {
                                                    parentTask: selectedTask.id,
                                                    id: crypto.randomUUID(),
                                                    projects: selectedTask.projects?.map(p => p.id),
                                                    task_type: selectedTask.task_type?.id
                                                }])
                                            }} icon="mi mi:add"
                                                  className="border-1 border-300 border-dashed border-round-2xl p-2 text-lg mt-2"/>

                                        </div>
                                    </div>
                                ) : (
                                    <Button onClick={() => {
                                        setSubTasks([{
                                            parentTask: selectedTask.id,
                                            id: crypto.randomUUID(),
                                            projects: selectedTask.projects?.map(p => p.id),
                                            task_type: selectedTask.task_type?.id
                                        }])
                                    }} label={translate("add-subtask")} className="text-xs gap-2 p-2 p-button-outlined"
                                            icon={<Icon icon="mi mi:add_task" className="text-lg"/>}/>
                                )}
                            </div>
                        </LabelValue>

                    </div>
                    </div>
                    <div className="w-full">
                        <Comments taskData={selectedTask} canManageOthers={!disabled}/>
                    </div>
                </div>

                <TaskDelayModal
                    visible={delayModalVisible}
                    setVisible={setDelayModalVisible}
                    task={selectedTask}
                    updateTask={updateTaskProp}
                    members={workspaceMembershipData?.workspace?.workspace_memberships || []}
                />
                {scopeDialog}

                {/*<Footer/>*/}
            </div>
        </Sidebar>
    );
}

function SelectDateTime({selectedDateTime, onChange}) {
    const [focused, setFocused] = useState(false);
    return(
        <div className={`flex align-items-center gap-1 cursor-pointer hover:surface-100 border-round p-1 mr-3 ml-3 ${isPast(new Date(selectedDateTime).getTime()/1000, 1, "days")&&"text-red-500"}`}>
            <Icon icon="mi mi:event"/>
            <Span focused={focused}
                  setFocused={setFocused}
                  inputValue={new Date(selectedDateTime).getTime()/1000}
                  inputClassName="text-xs bg-transparent"
                  spanClassName="text-xs p-1"
                  format="dddd، DD MMMM YYYY"
                  type="PERSIAN_DATE"
                  onChange={(e)=>{onChange({value:new Date(e.value*1000)})}}
            >
                {selectedDateTime}
            </Span>
        </div>
    )
}

function SelectDeliverables({projects = [], deliverables = [], onChange}) {
    return (
        <div>
            <MultiSelectProjectsDeliverables
                onChange={onChange}
                options={projects ?? []}
                value={deliverables ?? []}
            />
        </div>
    );
}



// function SelectDeliverables({projects, deliverables, onChange}) {
//     const ref = useRef()
//     return(
//         <div>
//             <div className="flex flex-wrap align-items-center gap-1">
//                 {deliverables?.map(d=>(<span className="text-xs px-2  py-1 bg-primary-50 border-1 border-primary border-round">{d.title}</span>))}
//                 <Icon icon="mi mi:add_circle" onClick={(e)=>{ref.current.toggle(e)}}/>
//             </div>
//             <OverlayPanel pt={{root:()=>({className:"p-0 rtl"}),content:()=>({className:"p-0"})}} ref={ref}>
//                 <MultiSelectProjectsDeliverables onChange={onChange} options={projects} value={deliverables} />
//             </OverlayPanel>
//         </div>
//
//     )
// }


function Message({title, icon, left}) {
    return(
        <div className="w-full flex align-items-center justify-content-between border-round-2xl surface-100 p-3 text-xs">
            <div className="flex align-items-center gap-2">
                {icon}
                <span>{title}</span>
            </div>
            {left??<span/>}

        </div>
    )
}

function TaskViewPopup({task, seenBy = []}) {
    const translate = useTranslate();
    const responsibleMembers = [task?.assignee, ...(task?.members ?? [])]
        .filter(Boolean)
        .filter((member, index, members) => members.findIndex((item) => item.id === member.id) === index);
    const getViewUser = (view) => view?.user ?? view;
    const getViewDate = (view) => view?.seenAt ?? view?.createdAt;
    const lastView = [...seenBy].sort((a, b) => new Date(getViewDate(b) ?? 0) - new Date(getViewDate(a) ?? 0))[0];
    const viewTime = (view) => {
        const date = getViewDate(view);
        return date ? timeSince(new Date(date).getTime() / 1000) : translate("task-view.seen");
    };

    return (
        <div className="absolute top-100 left-0 mt-2 w-20rem max-h-24rem overflow-auto surface-card border-1 border-200 border-round-xl shadow-4 z-5 rtl">
            <div className="p-3 border-bottom-1 border-100">
                <div className="text-2xs font-bold text-400 mb-2">{translate("task-view.creator")}</div>
                <div className="flex align-items-center gap-2">
                    <Avatar size="normal" memberData={task?.creatorUser?.member}/>
                    <div className="flex flex-column gap-1">
                        <span className="text-xs font-bold">{task?.creatorUser?.member?.fullname ?? "---"}</span>
                        {task?.createdAt && <Span spanClassName="text-2xs text-400" type="PERSIAN_DATE" format="YYYY/MM/DD HH:mm">{task.createdAt}</Span>}
                    </div>
                </div>
            </div>

            <div className="p-3 border-bottom-1 border-100">
                <div className="text-2xs font-bold text-400 mb-2">{translate("task-view.responsible-members")}</div>
                {responsibleMembers.length > 0 ? responsibleMembers.map((member) => {
                    const memberView = seenBy.find((view) => getViewUser(view)?.id === member.id);
                    return (
                        <div key={member.id} className="flex align-items-center justify-content-between gap-2 py-2">
                            <div className="flex align-items-center gap-2 min-w-0">
                                <Avatar size="small" memberData={member}/>
                                <span className="text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">{member.fullname ?? "---"}</span>
                            </div>
                            <span className="text-2xs text-400 white-space-nowrap">
                                {memberView ? viewTime(memberView) : translate("task-view.not-seen")}
                            </span>
                        </div>
                    );
                }) : <span className="text-xs text-400">{translate("task-view.no-responsible-members")}</span>}
            </div>

            <div className="p-3 surface-50">
                <div className="text-2xs font-bold text-400 mb-2">{translate("task-view.activity")}</div>
                <div className="flex flex-column gap-1 mb-3 text-2xs text-500">
                    <span>{translate("task-view.total").replace("{{COUNT}}", seenBy.length)}</span>
                    {lastView && (
                        <span>{translate("task-view.last")
                            .replace("{{NAME}}", getViewUser(lastView)?.fullname ?? "---")
                            .replace("{{TIME}}", viewTime(lastView))}</span>
                    )}
                </div>
                {seenBy.length > 0 ? seenBy.map((view, index) => {
                    const viewer = getViewUser(view);
                    return (
                        <div key={`${viewer?.id ?? "viewer"}-${index}`} className="flex align-items-center justify-content-between gap-2 py-2">
                            <div className="flex align-items-center gap-2 min-w-0">
                                <Avatar size="small" memberData={viewer}/>
                                <span className="text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">{viewer?.fullname ?? "---"}</span>
                            </div>
                            <span className="text-2xs text-400 white-space-nowrap">{viewTime(view)}</span>
                        </div>
                    );
                }) : <span className="text-xs text-400">{translate("task-view.no-activity")}</span>}
            </div>
        </div>
    );
}

function LabelValue({label, children, alignIcon = false}) {
    return(
        <div className="task-detail-row w-full grid align-items-start p-2 m-0" dir="rtl">
            <span className="col-fixed text-xs text-400 text-right pt-2 px-0" style={{width: "7rem"}}>{label}</span>
            <div
                className={`task-detail-field-value col min-w-0 p-0 flex align-items-center justify-content-start min-h-3rem text-right ${alignIcon ? "task-detail-field-value--align-icon" : ""}`}
                dir="rtl"
            >
                {children??<></>}
            </div>
        </div>
    )
}

export function normalizeChecklist(items) {
    return (Array.isArray(items) ? items : [])
        .map((item) => ({
            id: String(item?.id || "").trim(),
            title: String(item?.title || "").trim(),
            done: Boolean(item?.done),
        }))
        .filter((item) => item.id && item.title)
        .slice(0, 100);
}

function TaskChecklist({items, disabled, onChange}) {
    const translate = useTranslate();
    const [title, setTitle] = useState("");
    const safeItems = normalizeChecklist(items);
    const completed = safeItems.filter((item) => item.done).length;
    const addItem = () => {
        const nextTitle = title.trim();
        if (!nextTitle || disabled) return;
        onChange?.([...safeItems, {id: crypto.randomUUID(), title: nextTitle, done: false}]);
        setTitle("");
    };

    return (
        <div className="w-full flex flex-column gap-2" dir="rtl">
            {safeItems.length > 0 && (
                <div className="flex justify-content-between text-2xs text-500">
                    <span>{translate("task-checklist-progress")}</span>
                    <span>{completed} / {safeItems.length}</span>
                </div>
            )}
            {safeItems.map((item) => (
                <div key={item.id} className="w-full flex align-items-center justify-content-start gap-2 p-2 surface-50 border-round-lg">
                    <Checkbox
                        inputId={`task-checklist-${item.id}`}
                        checked={item.done}
                        disabled={disabled}
                        onChange={(event) => onChange?.(safeItems.map((current) => current.id === item.id ? {...current, done: event.checked} : current))}
                    />
                    <label htmlFor={`task-checklist-${item.id}`} className={`flex-1 text-xs text-right ${item.done ? "line-through text-400" : "text-700"}`}>{item.title}</label>
                    <Button
                        type="button"
                        disabled={disabled}
                        text
                        severity="danger"
                        className="w-2rem h-2rem p-0"
                        icon={<Icon icon="mi mi:delete" className="text-sm"/>}
                        aria-label={translate("delete-checklist-item")}
                        onClick={() => onChange?.(safeItems.filter((current) => current.id !== item.id))}
                    />
                </div>
            ))}
            <div className="flex align-items-center gap-2 w-full">
                <InputText
                    value={title}
                    disabled={disabled}
                    maxLength={160}
                    className="flex-1 text-xs text-right"
                    placeholder={translate("new-checklist-item")}
                    onChange={(event) => setTitle(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            addItem();
                        }
                    }}
                />
                <Button
                    type="button"
                    disabled={disabled || !title.trim()}
                    outlined
                    className="w-2rem h-2rem p-0"
                    icon={<Icon icon="mi mi:add"/>}
                    aria-label={translate("add-checklist-item")}
                    onClick={addItem}
                />
            </div>
        </div>
    );
}

function Comments({taskData, canManageOthers}) {
    const translate = useTranslate();
    const {workspaceMembershipData, workspaceId} = useSelector((state) => state.app);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const taskComment = useTaskComment();
    const activeWorkspaceId = workspaceMembershipData?.workspace?.documentId || workspaceId;
    const members = useMemo(() => (
        (workspaceMembershipData?.workspace?.workspace_memberships || [])
            .map((membership) => membership?.member)
            .filter((member) => member?.id)
    ), [workspaceMembershipData]);

    const ItemTemplate = (data)=>{
        return (
            <EditableComment
                comment={data}
                canManageOthers={canManageOthers}
                onError={setError}
                onUpdate={async (comment, text) => {
                    const updated = await taskComment.updateTaskComment(comment.documentId || comment.id, {text});
                    setComments((current) => current.map((item) => (
                        (item.documentId || item.id) === (comment.documentId || comment.id) ? updated : item
                    )));
                }}
                onDelete={async (comment) => {
                    await taskComment.deleteTaskComment(comment.documentId || comment.id);
                    setComments((current) => current.filter((item) => (
                        (item.documentId || item.id) !== (comment.documentId || comment.id)
                    )));
                }}
            >
                <div className="p-2 w-full border-round surface-50 flex flex-column gap-2">
                            {data.text && <MessageContent content={data.text} mentions={data.mentions}/>}
                            {(data.attachments || []).map((file) => (
                                <ChatMessageAttachment
                                    key={file?.documentId || file?.id}
                                    file={file}
                                    workspaceId={activeWorkspaceId}
                                    onError={() => setError(translate("task-comment.error.attachment-access"))}
                                />
                            ))}
                            {!data.text && !(data.attachments || []).length && (
                                <span className="text-xs text-400">{translate("task-comment.empty-content")}</span>
                            )}
                </div>
            </EditableComment>
        )
    }

    useEffect(() => {
        if (!taskData?.id) {
            setComments([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError("");
        taskComment.getTaskComments(taskData.id)
            .then((data) => {
                if (!cancelled) setComments(data || []);
            })
            .catch(() => {
                if (!cancelled) setError(translate("task-comment.error.load"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // The model hook has an unstable identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskData?.id]);

    return(
        <div className="p-3 flex flex-column w-full gap-3 overflow-visible">
            <div className="w-full flex-shrink-0">
                <CreateTaskComment
                    taskData={taskData}
                    members={members}
                    onSubmit={(comment) => setComments((current) => [...current, comment])}
                    onError={setError}
                />
            </div>

            {error && <span role="alert" className="text-xs text-red-500 px-2">{error}</span>}

            {loading ? (
                <div className="flex align-items-center justify-content-center p-3">
                    <Icon icon="mi mi:progress_activity" className="pi-spin text-primary text-xl"/>
                </div>
            ) : (
                <DataView emptyMessage={<></>} itemTemplate={ItemTemplate} layout="list" value={comments}/>
            )}
        </div>
    )
}

function CreateTaskComment({taskData, members, onSubmit, onError}) {
    const translate = useTranslate();
    const [text, setText] = useState("");
    const [mentions, setMentions] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [uploadVisible, setUploadVisible] = useState(false);
    const [sending, setSending] = useState(false);
    const taskComment = useTaskComment();
    const permission = usePermission();
    const canAttach = permission.can("upload-file");

    useEffect(() => {
        setText("");
        setMentions([]);
        setAttachments([]);
        setUploadVisible(false);
    }, [taskData?.id]);

    const createComment = async ()=>{
        if (sending || (!text.trim() && !attachments.length)) return;
        setSending(true);
        onError?.("");
        try {
            const comment = await taskComment.createTaskComment({
                task: taskData.id,
                text: text.trim() || null,
                mentions: mentions.map((mention) => ({id: mention.id, token: mention.token})),
                attachments: chatMessageAttachmentIdentifiers(attachments),
            });
            if (!comment) throw new Error("task-comment.error.create");
            onSubmit?.(comment);
            setText("");
            setMentions([]);
            setAttachments([]);
        } catch (error) {
            const key = error?.message || "task-comment.error.create";
            const translated = translate(key);
            onError?.(translated === key ? translate("task-comment.error.create") : translated);
        } finally {
            setSending(false);
        }
    }

    const handleChange = (event) => {
        const value = event.target.value;
        setText(value);
        setMentions((current) => current.filter((mention) => (
            value.includes(`@${mention.token || mentionToken(mention)}`)
        )));
    };

    const handleSelect = ({suggestion}) => {
        if (suggestion?.suggestionType !== "mention") return;
        const mention = {
            id: suggestion.id,
            documentId: suggestion.documentId,
            fullname: suggestion.fullname,
            username: suggestion.username,
            token: suggestion.insertText,
        };
        setMentions((current) => current.some((item) => String(item.id) === String(mention.id))
            ? current
            : [...current, mention]);
    };

    const removeAttachment = (attachment) => {
        const key = attachment?.documentId ?? attachment?.id;
        setAttachments((current) => current.filter((item) => (
            String(item?.documentId ?? item?.id) !== String(key)
        )));
    };

    return(
        <>
            <ChatComposer
                value={text}
                members={members}
                references={[]}
                attachments={attachments}
                onChange={handleChange}
                onSelect={handleSelect}
                onRemoveAttachment={removeAttachment}
                canAttach={canAttach}
                onAttach={() => setUploadVisible(true)}
                onSubmit={createComment}
                submitDisabled={!text.trim() && !attachments.length}
                loading={sending}
                disabled={sending}
                placeholder={translate("task-comment.composer.placeholder")}
                quickActionTypes={["mention"]}
                triggers={["@"]}
            />
            <UploadDialog
                visible={uploadVisible}
                setVisible={setUploadVisible}
                multiple
                entityType="task"
                entityDocumentId={taskData?.documentId || taskData?.id}
                purpose="attachment"
                updateContentType={(files) => setAttachments((current) => {
                    const existing = new Set(current.map((item) => String(item?.documentId ?? item?.id)));
                    return [
                        ...current,
                        ...files.filter((item) => !existing.has(String(item?.documentId ?? item?.id))),
                    ];
                })}
            />
        </>
    )
}

export default TaskSidebar;
